// Companion/blocks/chatBlockVisibility.tsx
//
// Whether the block a renderer is drawing is actually on screen.
//
// A turn that serves two blocks of one kind shows the first and puts the rest
// behind a "Reveal" chip (`ChatBlocks`), and a hidden block is HIDDEN, not
// absent: it is rendered and mounted, with the `hidden` attribute and
// `INERT_PROPS` keeping it out of the page's eye, mouse and keyboard. That is
// the right trade for content the payload already holds — the block has nothing
// to fetch and appears instantly when the chip is pressed.
//
// It is the wrong trade for a block whose mount *starts work*. A chat-authored
// draft asks the server to write its question when it mounts, which costs a
// model call and a slot of the student's daily allowance; doing that for a
// question nobody has asked to see would spend both on nothing. So the renderer
// has to be able to ask, and this is how: `ChatBlocks` publishes the visibility
// it already computes, and a renderer that cares reads it.
//
// `resolveChatBlock` builds the body before the reveal state is known, which is
// why this is a context rather than another field on the render scope.

import { createContext, useContext } from 'react';

export const ChatBlockVisibilityContext = createContext<{ isHidden: boolean }>({
  isHidden: false,
});

/** True while this block sits behind an unopened reveal chip. */
export const useIsChatBlockHidden = (): boolean =>
  useContext(ChatBlockVisibilityContext).isHidden;
